import { authService } from '../services/auth.ts';
import { icons } from './library/ui/icons.ts';
import './library/ui/app-icon.ts';
import { ToastService } from '../services/toast.ts';
import './user-menu.ts';
import '../pages/app/gateway/gateway.ts';
import '../pages/app/secrets/secrets.ts';
import '../pages/app/alerts/alerts.ts';
import '../pages/app/profile/profile.ts';
import '../pages/app/organization/organization.ts';
import '../pages/app/overview/overview.ts';
import '../pages/app/identity/identity.ts';

export class AppShell extends HTMLElement {
  private _currentView: string = 'overview';
  private _isPanelCollapsed = false;
  private _isInitialized = false;
  private _profile: any = null;
  private _currentPanel: any = null;
  private _lastPanelEvent: any = null;

  constructor() {
    super();
  }

  async connectedCallback() {
    console.log("AppShell connectedCallback", { isInitialized: this._isInitialized });
    if (this._isInitialized) return;
    this._isInitialized = true;

    await this.loadProfile();
    this.setupEventListeners();
    await this.initRoute();

    window.addEventListener('auth-state-changed', this.handleAuthStateChange);
    window.addEventListener('popstate', () => this.initRoute());
  }

  disconnectedCallback() {
    window.removeEventListener('auth-state-changed', this.handleAuthStateChange);
    window.removeEventListener('popstate', () => this.initRoute());
  }

  private handleAuthStateChange = async () => {
    await this.loadProfile();
    await this.initRoute(); // Re-initialize route to pick up new orgId if needed
    this.updateWorkspace(); // Force workspace update to sync org-id attributes
  };

  private async loadProfile() {
    try {
      this._profile = await authService.getProfile();
      this.render(); // Re-render to update avatar if needed
    } catch (e) {
      console.error('AppShell: Failed to load profile', e);
    }
  }

  private setupEventListeners() {
    this.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;

      // Panel Toggle
      if (target.closest('#toggle-panel')) {
        console.log("Toggle clicked. Old state:", this._isPanelCollapsed);
        this._isPanelCollapsed = !this._isPanelCollapsed;
        console.log("New state:", this._isPanelCollapsed);
        this.updateWorkspace();
        return;
      }


      // Navigation
      const block = target.closest('.control-block') as HTMLElement;
      if (block) {
        const view = block.getAttribute('data-view');
        if (view === 'docs') {
          window.open('https://crate.cc/docs', '_blank');
          return;
        }
        if (view === 'profile') {
          e.stopPropagation();
          const userMenu = this.querySelector('#user-menu') as any;
          if (userMenu) {
            userMenu.toggleMenu();
          }
          return;
        }
        if (view) {
          const tab = block.getAttribute('data-tab');
          this.setView(view, tab ? { tab } : null);
        }
      }
    });

    window.addEventListener('navigate', (e: any) => {
      if (e.detail && e.detail.view) {
        this.setView(e.detail.view, e.detail.params);
      }
    });

    this.addEventListener('shell-panel-changed', (e: any) => {
      console.log("AppShell: shell-panel-changed received", e.detail);
      this._lastPanelEvent = e;
      this.applyPanel();
    });

    window.addEventListener('unentitled-access', (e: any) => {
      // Toast removed per user request
    });
  }

  private applyPanel() {
    if (!this._lastPanelEvent) return;
    
    const panel = this.querySelector('.view-panel-content');
    if (panel) {
      panel.innerHTML = '';
      const content = this._lastPanelEvent.detail?.panel || this._lastPanelEvent.detail?.content;
      if (content) {
        if (typeof content === 'string') {
          panel.innerHTML = content;
        } else if (content instanceof HTMLElement) {
          panel.appendChild(content);
        }
        const aside = this.querySelector('.view-panel') as HTMLElement;
        if (aside) aside.style.display = 'block';
      } else {
        const aside = this.querySelector('.view-panel') as HTMLElement;
        if (aside) aside.style.display = 'none';
      }
    }

    // Handle dynamic header
    const headerTitle = this.querySelector('#view-title') as HTMLElement;
    const headerSubtitle = this.querySelector('#view-subtitle') as HTMLElement;
    if (headerTitle) {
      const customHeader = this._lastPanelEvent.detail?.header;
      if (customHeader) {
        headerTitle.innerHTML = customHeader.title;
        headerTitle.style.textTransform = 'none';
        if (headerSubtitle) {
          headerSubtitle.innerHTML = customHeader.subtitle || '';
          headerSubtitle.style.display = 'block';
        }
      } else {
        headerTitle.textContent = this._currentView === 'overview' ? 'CRATE' : `[ ${this._currentView} ]`;
        headerTitle.style.textTransform = 'uppercase';
        if (headerSubtitle) headerSubtitle.style.display = 'none';
      }
    }
  }

  private _isInitializing = false;
  private async initRoute() {
    if (this._isInitializing) return;
    this._isInitializing = true;
    
    try {
      let path = window.location.pathname;
      if (path.startsWith('/')) path = path.substring(1);

      if (!path) {
        this.setView('overview');
        return;
      }

      const segments = path.split('/');
    const view = segments[0];
    const orgTabs = ['general', 'users', 'audit', 'billing'];
    const gatewayTabs = ['metrics', 'domains', 'routing', 'plugins', 'history'];

    let tab = '';

    if (view === 'organization') {
      tab = segments[1] || 'general';
    } else if (view === 'gateway') {
      tab = segments[1] || 'metrics';
    } else if (view === 'profile') {
      tab = segments[1] || 'general';
    } else if (view === 'identity') {
      tab = segments[1] || 'metrics';
    }

    const params: any = {};
    if (tab) params.tab = tab;

    this.setView(view, params, true);
    } finally {
      this._isInitializing = false;
    }
  }

  private _viewParams: any = null;

  private setView(view: string, params: any = null, fromHistory = false) {
    if (this._currentView === view && JSON.stringify(this._viewParams) === JSON.stringify(params)) return;
    this._currentView = view;
    this._viewParams = params;
    this._lastPanelEvent = null; // Clear panel when changing main view

    if (!fromHistory) {
      const tabPart = params?.tab ? `/${params.tab}` : '';
      const path = `/${view}${tabPart}`;
      history.pushState(null, '', path);
    }

    // Update navigation active state
    this.querySelectorAll('.control-block').forEach(b => {
      const bView = b.getAttribute('data-view');
      const bTab = b.getAttribute('data-tab');
      const isActive = bView === view && (!bTab || bTab === params?.tab);
      b.classList.toggle('active', isActive);
    });

    this.updateWorkspace();
  }

  private updateWorkspace() {
    const workspace = this.querySelector('.workspace') as HTMLElement;
    if (!workspace) return;

    const mockCards = Array.from({ length: 12 }).map((_, i) => `
      <div class="technical-card sketch-border" style="
        padding: 24px;
        margin-bottom: 24px;
        background: rgba(255, 255, 255, 0.02);
      ">
        <div style="font-family: 'JetBrains Mono', monospace; font-size: 0.6rem; color: var(--accent); margin-bottom: 8px;">
          REF_ID: CRATE-00${i + 1}
        </div>
        <div style="font-family: 'JetBrains Mono', monospace; font-size: 0.9rem; color: white; margin-bottom: 12px; text-transform: uppercase;">
          System Module Component ${i + 1}
        </div>
        <div style="height: 2px; width: 40px; background: var(--accent); margin-bottom: 16px; opacity: 0.5;"></div>
        <div style="font-size: 0.8rem; color: var(--text-dim); line-height: 1.6;">
          This is a technical drafting block used to simulate complex interface data. It helps verify grid alignment, scroll performance, and responsive layout transitions when the side options panel is toggled.
        </div>
      </div>
    `).join('');

    const viewsWithPanel = ['gateway', 'profile', 'organization', 'secrets', 'identity'];
    const showPanel = viewsWithPanel.includes(this._currentView);

    const currentViewAttr = workspace.getAttribute('data-view');
    const currentParamsAttr = workspace.getAttribute('data-params');
    const paramsStr = JSON.stringify(this._viewParams);

    // If view type is the same, just update attributes on the existing element
    if (currentViewAttr === this._currentView) {
      const viewEl = workspace.querySelector(`.view-body > *:first-child`);
      if (viewEl) {
        let changed = false;
        if (this._viewParams) {
          Object.keys(this._viewParams).forEach(key => {
            const attrName = key.replace(/[A-Z]/g, m => "-" + m.toLowerCase());
            const val = this._viewParams[key];
            if (val !== undefined && val !== null) {
              viewEl.setAttribute(attrName, String(val));
            } else {
              viewEl.removeAttribute(attrName);
            }
            changed = true;
          });
        }

        // Force sync org-id for org-scoped views when session changes
        const activeOrgId = this._viewParams?.orgId || this._profile?.org_id || localStorage.getItem('crate_org_id') || '';
        if (['app-gateway', 'app-organization', 'app-secrets', 'app-alerts', 'app-identity'].includes(viewEl.tagName.toLowerCase())) {
            if (viewEl.getAttribute('org-id') !== activeOrgId) {
                viewEl.setAttribute('org-id', activeOrgId);
                changed = true;
            }
        }

        if (currentParamsAttr !== paramsStr) {
          workspace.setAttribute('data-params', paramsStr);
        }
        
        // Update panel visibility
        const aside = workspace.querySelector('.view-panel') as HTMLElement;
        if (aside) {
          aside.style.display = showPanel ? 'block' : 'none';
        }

        // Sync global toggle visibility
        const globalToggle = this.querySelector('#toggle-panel') as HTMLElement;
        if (globalToggle) {
          globalToggle.style.display = showPanel ? 'flex' : 'none';
        }
        workspace.setAttribute('data-collapsed', this._isPanelCollapsed.toString());

        if (this._lastPanelEvent) {
          this.applyPanel();
        }
        return; 
      }
    }

    if (currentViewAttr !== this._currentView || currentParamsAttr !== paramsStr || !workspace.innerHTML.trim()) {
      const activeOrgId = this._viewParams?.orgId || this._profile?.org_id || localStorage.getItem('crate_org_id') || '';
      let viewBody = '';
      switch (this._currentView) {
        case 'gateway':
          viewBody = `<app-gateway org-id="${activeOrgId}" tab="${this._viewParams?.tab || 'metrics'}"></app-gateway>`;
          break;
        case 'identity':
          viewBody = `<app-identity org-id="${activeOrgId}" tab="${this._viewParams?.tab || 'metrics'}"></app-identity>`;
          break;
        case 'organization':
          viewBody = `<app-organization org-id="${activeOrgId}" tab="${this._viewParams?.tab || 'general'}"></app-organization>`;
          break;
        case 'profile':
          viewBody = `<app-profile tab="${this._viewParams?.tab || 'general'}"></app-profile>`;
          break;
        case 'secrets':
          viewBody = `<app-secrets org-id="${activeOrgId}"></app-secrets>`;
          break;
        case 'alerts':
          viewBody = `<app-alerts org-id="${activeOrgId}"></app-alerts>`;
          break;
        case 'overview':
          viewBody = `<app-overview></app-overview>`;
          break;
        default:
          viewBody = `<app-overview></app-overview>`;
      }

      workspace.innerHTML = `
        <header class="view-header drafting-grid">
          <div style="display: flex; flex-direction: column; justify-content: center; gap: 0.25rem;">
            <h1 id="view-title" style="font-family: 'JetBrains Mono', monospace; font-size: 1.5rem; text-transform: uppercase; letter-spacing: 0.1em; color: var(--accent); margin: 0; line-height: 1;">
              ${this._currentView === 'overview' ? 'CRATE' : `[ ${this._currentView} ]`}
            </h1>
            <div id="view-subtitle" style="font-family: var(--font-mono); font-size: 0.85rem; color: white; display: none; opacity: 0.7; letter-spacing: 0.05em;"></div>
          </div>
        </header>
        <div class="workspace-body">
          <div class="view-content">
            <div class="view-body">
              ${viewBody}
            </div>
          </div>
          ${showPanel ? `
            <aside class="view-panel">
              <div class="view-panel-inner">
                <div class="view-panel-content">
                  <!-- Populated by child components via shell-panel-changed event -->
                </div>
              </div>
            </aside>
          ` : ''}
        </div>
      `;
      workspace.setAttribute('data-view', this._currentView);
      workspace.setAttribute('data-params', paramsStr);
    }

    // Sync global toggle visibility
    const globalToggle = this.querySelector('#toggle-panel') as HTMLElement;
    if (globalToggle) {
      globalToggle.style.display = showPanel ? 'flex' : 'none';
      globalToggle.title = this._isPanelCollapsed ? 'Show Options' : 'Hide Options';
    }

    workspace.setAttribute('data-collapsed', this._isPanelCollapsed.toString());

    // Try to restore panel if we have one
    if (this._lastPanelEvent) {
      this.applyPanel();
    }
  }

  private renderDoodles() {
    return `
      <div class="doodle-container">
        <!-- Box Doodle -->
        <div class="doodle doodle-lg doodle-animated" style="top: 12%; right: 8%; opacity: 0.14;">
          <svg viewBox="0 0 80 80" fill="none">
            <path d="M15 30L15 65L65 65L65 30L15 30Z" stroke="#f59e0b" stroke-width="2" style="filter: url(#sketchy);" stroke-linecap="round" stroke-linejoin="round"></path>
            <path d="M17 42L63 42" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round"></path>
            <path d="M17 52L63 52" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round"></path>
            <circle cx="32" cy="24" r="4" stroke="#f59e0b" stroke-width="1.5" fill="none"></circle>
            <circle cx="32" cy="23" r="1.5" fill="#f59e0b"></circle>
            <circle cx="50" cy="24" r="4" stroke="#f59e0b" stroke-width="1.5" fill="none"></circle>
            <circle cx="50" cy="23" r="1.5" fill="#f59e0b"></circle>
          </svg>
        </div>

        <!-- Coffee/Cup Doodle -->
        <div class="doodle doodle-md doodle-animated" style="bottom: 15%; right: 5%; opacity: 0.12; animation-delay: -2s;">
          <svg viewBox="0 0 60 60" fill="none">
            <path d="M12 20L16 52C16 55 18 56 22 56L40 56C44 56 46 55 46 52L50 20" stroke="#f59e0b" stroke-width="2" style="filter: url(#sketchy);" stroke-linecap="round" stroke-linejoin="round"></path>
            <path d="M50 25C56 25 58 30 58 35C58 40 56 45 50 45" stroke="#f59e0b" stroke-width="2" style="filter: url(#sketchy);" stroke-linecap="round"></path>
            <path d="M22 16C22 12 26 10 26 6" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round" opacity="0.6"></path>
            <path d="M31 14C31 10 35 8 35 4" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round" opacity="0.4"></path>
            <path d="M40 16C40 12 44 10 44 6" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round" opacity="0.6"></path>
          </svg>
        </div>

        <!-- Lightning Doodle -->
        <div class="doodle doodle-md" style="top: 55%; left: 90px; opacity: 0.10; transform: rotate(12deg);">
          <svg viewBox="0 0 40 70" fill="none">
            <path d="M22 2L8 30L20 28L12 65" stroke="#f59e0b" stroke-width="2.5" style="filter: url(#sketchy);" stroke-linecap="round" stroke-linejoin="round"></path>
            <path d="M24 5L10 32L22 30L14 62" stroke="#f59e0b" stroke-width="1" stroke-linecap="round" opacity="0.4"></path>
          </svg>
        </div>

        <!-- Gear Doodle -->
        <div class="doodle doodle-md" style="bottom: 30%; right: 20%; opacity: 0.11;">
          <svg viewBox="0 0 60 60" fill="none">
            <path d="M26 5L34 5L36 12L42 14L48 8L54 14L48 20L50 26L57 28L57 36L50 38L48 44L54 50L48 56L42 50L36 52L34 59L26 59L24 52L18 50L12 56L6 50L12 44L10 38L3 36L3 28L10 26L12 20L6 14L12 8L18 14L24 12Z" stroke="#f59e0b" stroke-width="1.8" style="filter: url(#sketchy);" stroke-linecap="round" stroke-linejoin="round"></path>
            <circle cx="30" cy="32" r="8" stroke="#f59e0b" stroke-width="1.5"></circle>
          </svg>
        </div>

        <!-- Octopus/Cratopus Doodle -->
        <div class="doodle doodle-lg doodle-animated" style="top: 40%; right: 3%; opacity: 0.16; animation-delay: -4s;">
          <svg viewBox="0 0 100 100" fill="none">
            <path d="M50 20C35 20 25 30 25 45C25 60 35 70 50 70C65 70 75 60 75 45C75 30 65 20 50 20Z" stroke="#f59e0b" stroke-width="2" style="filter: url(#sketchy);" stroke-linecap="round"></path>
            <circle cx="42" cy="40" r="3" fill="#f59e0b"></circle>
            <circle cx="58" cy="40" r="3" fill="#f59e0b"></circle>
            <path d="M30 65C25 75 15 78 10 75" stroke="#f59e0b" stroke-width="2" style="filter: url(#sketchy);" stroke-linecap="round"></path>
            <path d="M40 70C38 82 45 90 55 92" stroke="#f59e0b" stroke-width="2" style="filter: url(#sketchy);" stroke-linecap="round"></path>
            <path d="M60 70C62 82 75 88 85 85" stroke="#f59e0b" stroke-width="2" style="filter: url(#sketchy);" stroke-linecap="round"></path>
            <path d="M70 65C75 75 85 78 90 75" stroke="#f59e0b" stroke-width="2" style="filter: url(#sketchy);" stroke-linecap="round"></path>
            <path d="M45 55C47 58 53 58 55 55" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round"></path>
          </svg>
        </div>
      </div>
    `;
  }

  render() {
    const avatarUrl = this._profile?.gravatar 
        ? `https://www.gravatar.com/avatar/${this._profile.gravatar}?d=identicon&s=32`
        : `https://www.gravatar.com/avatar/00000000000000000000000000000000?d=identicon&s=32`;

    this.innerHTML = `
      ${this.renderDoodles()}
      <div style="position: relative; width: 100vw; height: 100vh; overflow: hidden;">
        <user-menu id="user-menu"></user-menu>
        <div class="app-layout drafting-grid">
          <!-- Global Panel Toggle (Fixed) -->
          <button class="panel-icon-btn" id="toggle-panel" style="display: none;">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M15 3v18" />
            </svg>
          </button>

          <!-- Control Strip (60px) -->
          <aside class="control-strip">
            <!-- Top: Logo -->
            <div class="control-block logo-block" data-view="overview">
              <img src="/images/crate_sketch.png" alt="Crate" style="width: 40px; height: 40px;" />
            </div>

            <!-- Middle: Navigation -->
            <nav class="nav-strip">
              <div class="control-block ${this._currentView === 'gateway' ? 'active' : ''}" 
                   data-view="gateway" data-tooltip="Gateway">
                <app-icon name="route" size="md"></app-icon>
              </div>
              <div class="control-block ${this._currentView === 'identity' ? 'active' : ''}" 
                   data-view="identity" data-tooltip="Identity">
                <app-icon name="users" size="md"></app-icon>
              </div>
              <div class="control-block ${this._currentView === 'secrets' ? 'active' : ''}" 
                   data-view="secrets" data-tooltip="Secrets">
                <app-icon name="lock" size="md"></app-icon>
              </div>
              <div class="control-block ${this._currentView === 'alerts' ? 'active' : ''}" 
                   data-view="alerts" data-tooltip="Alerts">
                <app-icon name="bell" size="md"></app-icon>
              </div>
            </nav>

            <!-- Bottom-1: Docs -->
            <div class="control-block" data-view="docs" data-tooltip="Documentation">
              <app-icon name="book" size="md"></app-icon>
            </div>

            <!-- Bottom: User -->
            <div class="control-block user-block ${this._currentView === 'profile' ? 'active' : ''}" data-view="profile" data-tooltip="User Settings">
              <img src="${avatarUrl}" alt="User" style="width: 32px; height: 32px; border-radius: 50%; border: 1px solid var(--border-sketch);" />
            </div>
          </aside>

          <!-- Main Workspace -->
          <main class="workspace"></main>
        </div>
      </div>
    `;

    this.updateWorkspace();
  }
}

if (!customElements.get('app-shell')) {
  customElements.define('app-shell', AppShell);
}